<template>
  <div class="pdt-20px column is-12">
    <div class="card">
      <header class="header-card">
        <p>
          {{ $t('echeancierPaiement.informationGeneral.title')}}
        </p>
      </header>
      <div class="card-content">
        <div class="columns is-multiline">
          <div class="column is-2">
            <user-selector
              v-model="factureDetail.commercialId"
              :critere="(u) => [constantes.COMMERCIAL, constantes.DIRECTEUR_COMMERCIAL]
                .includes(u.profil)"
              :placeholder="$t('editionCommande.informations.aucunCommercial')"
              :label="$t('echeancierPaiement.informationGeneral.commercial')"
              :disabled="isCommercial"
            />
          </div>
          <div class="column is-1">
            <b-field :label="$t('echeancierPaiement.informationGeneral.tauxCom')">
              <input-currency v-model="factureDetail.commissionCommercial" isNumber="true">
              </input-currency>
              <span class="icon is-right pdt-15px">
                  <i>%</i>
                </span>
            </b-field>
          </div>
          <div class="column is-2">
            <user-selector
              v-model="factureDetail.assistantId"
              :critere="(u) => [constantes.ASSISTANT_COMMERCIAL]
                .includes(u.profil)"
              :label="$t('echeancierPaiement.informationGeneral.assistantCommercial')"
              :placeholder="$t('echeancierPaiement.informationGeneral.aucunAssistant')"
            />
          </div>
          <div class="column is-3" v-if="agencesSelectionnees.length > 1">
            <selection-agence v-model="factureDetail.agenceId"
                              obligatoire set-first-agence-find>
            </selection-agence>
          </div>
          <div class="column is-4">
            <b-field :label="$t('echeancierPaiement.informationGeneral.theme')">
              <b-input v-model="factureDetail.theme"></b-input>
            </b-field>
          </div>
          <div class="column is-3" v-if="modulePlanMediaActive">
            <b-field :label="$t('echeancierPaiement.informationGeneral.planMedia')">
              <b-autocomplete
                :value="searchPlanMedia"
                @select="setPlanMedia"
                :data="autocompletePlanMedia"
                field="code"
                icon="magnify"
                type="is-primary"
                :loading="isFetching"
                :open-on-focus="true"
                @input="getAsyncPlanMediaData"
                >
                <template slot="empty">
                  <div v-if="!isFetching">
                    {{$t('general.autocomplete.noRslt')}}
                  </div>
                  <div v-else>
                    {{$t('general.autocomplete.search')}}
                  </div>
                </template>
              </b-autocomplete>
            </b-field>
          </div>
          <div class="column is-3">
            <b-field :label="$t('echeancierPaiement.informationGeneral.tags')">
              <query-tags-tag-input
                :tooltipLabel="$t('echeancierPaiement.informationGeneral.tagInfo')"
                position="is-top"
                v-model="factureDetail.tags"
                field="tag"
                :moduleTag="constantes.DOCUMENT_VENTE">
              </query-tags-tag-input>
            </b-field>
          </div>
          <div class="column is-3">
            <b-field
              :label="$t('echeancierPaiement.informationGeneral.applicationTva')"
              class="obligatoire">
              <b-select v-model="applicationTvaAnnonceur" expanded>
                <option
                  v-for="item in constantes.LISTE_APPLICATION_TVA"
                  :value="item.code"
                  :key="item.code">
                  {{ $t(item.libelle) }}
                </option>
              </b-select>
            </b-field>
          </div>
        </div>
      </div>
    </div>
    <commentaires
      class="mgt-20px"
      :type="factureDetail.type"
      :devisCommandeFacture="factureDetail">
    </commentaires>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
import debounce from 'lodash/debounce';
import { RESULTS_PER_PAGE } from '@/configuration/clientTableConf';
import commentaires
  from '@/components/bodac/dcf/addEditDcf/commentaires/commentaires';
import SelectionAgence from '@/components/general/selectionAgence';
import { COMMERCIAL, DIRECTEUR_COMMERCIAL, ASSISTANT_COMMERCIAL } from '@/configuration/parametrage/utilisateurConf';
import serverTableDataTransformer from '@/services/planMedia/serverTableDataTransformer';
import { PARAMETRES_INTERNE_KEYS } from '@/store/modules/parametres-mutation-type';
import { DOCUMENT_VENTE } from '@/configuration/parametrage/tagConf';
import { LISTE_APPLICATION_TVA } from '@/configuration/tiers/tiersConf';
import queryTagsTagInput from '@/components/general/tagInput/queryTagsTagInput';
import messageErreur from '@/services/general/messageErreur';
import inputCurrency from '@/components/general/inputCurrency';
import userSelector from '@/components/commun/user/userSelector';
import { getAll as getAllPlanMedia } from '@/api/planMedia';

const Societe = createNamespacedHelpers('societe');
const User = createNamespacedHelpers('user');
const Parametres = createNamespacedHelpers('parametres');
const PlanMedia = createNamespacedHelpers('planMedia');

export default {
  props: {
    factureDetail: {
      type: Object,
      required: true,
    },
  },
  components: {
    commentaires,
    SelectionAgence,
    inputCurrency,
    userSelector,
    queryTagsTagInput,
  },
  data() {
    return {
      searchPlanMedia: '',
      autocompletePlanMedia: [],
      isFetching: false,
      constantes: {
        COMMERCIAL,
        DIRECTEUR_COMMERCIAL,
        ASSISTANT_COMMERCIAL,
        DOCUMENT_VENTE,
        LISTE_APPLICATION_TVA,
      },
    };
  },
  methods: {
    ...PlanMedia.mapActions({
      actionLoadItems: 'loadItems',
    }),
    getAsyncPlanMediaData: debounce(async function getData(text) {
      if (text === this.searchPlanMedia) return;
      try {
        this.isFetchingPlanMedia = true;
        const payload = {
          skipCount: 0,
          maxResultCount: RESULTS_PER_PAGE,
          query: {
            auto: text,
            societeId: this.societeActiveId,
          },
        };
        serverTableDataTransformer(payload);
        this.autocompletePlanMedia = (await getAllPlanMedia(payload))
          .data.result.items;
        this.isFetching = false;
      } catch (e) {
        messageErreur(this.$i18n);
        this.isFetching = false;
        throw e;
      }
    }),
    setPlanMedia(planMedia) {
      if (!planMedia) return;
      this.searchPlanMedia = planMedia.code;
      this.factureDetail.planMediaId = planMedia.id;
      this.factureDetail.planMedia = planMedia;
    },
  },
  computed: {
    ...User.mapGetters({
      utilisateur: 'user',
    }),
    ...Parametres.mapGetters({
      parametre: 'parametre',
      parametresInterne: 'parametresInterne',
    }),
    ...Societe.mapGetters({
      societeActive: 'societeActive',
      societeActiveId: 'societeActiveId',
      agencesSelectionnees: 'agenceActive',
    }),
    modulePlanMediaActive() {
      return this.parametresInterne(PARAMETRES_INTERNE_KEYS.modulePlanMedia.key);
    },
    isCommercial() {
      return (this.utilisateur.profil === COMMERCIAL
          && this.utilisateur.id === this.factureDetail.commercialId);
    },
    applicationTvaAnnonceur: {
      get() {
        const annonceur = this.factureDetail.intervenants.find(el => el.annonceur);
        return annonceur.applicationTva;
      },
      set(val) {
        const index = this.factureDetail.intervenants.findIndex(el => el.annonceur);
        this.factureDetail.intervenants[index].applicationTva = val;
      },
    },
  },
  mounted() {
    this.searchPlanMedia = this.factureDetail?.planMedia?.code ?? '';
  },
};

</script>
